import { buildApis } from '@sparklayer-io/dashboard/runtime';
import type { SiteType, UserType } from '@sparklayer-io/spark-dash-core/context/page-context';
import type { WithPageProvidersProps } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { type ComponentType, useMemo } from 'react';
import { apiHandler } from '../lib/handlers/api';
import { authHandler } from '../lib/handlers/auth';
import { createHostNavigationHandler, type HostNavContext } from '../lib/handlers/navigation';
import { notificationHandler } from '../lib/handlers/notification';
import { defaultSiteEnv, useAuthStore, useEnvStore } from '../lib/session';

export type HostSession = {
  siteId: string;
  siteEnv: string;
  env: NonNullable<ReturnType<typeof useHostSession>['env']>;
  user: NonNullable<ReturnType<typeof useHostSession>['user']>;
};

/** A site a page runs against that the URL does not name (e.g. `/help`, which resolves the user's
 * current team). Handed to `useHostSession` in place of the route's `siteId`/`siteEnv` params. */
export type HostSessionScope = {
  siteId: string;
  siteEnv?: string;
};

/**
 * The current session for a page under the `$siteId/$siteEnv` layout: the route params plus the
 * auth/env store state the guards already loaded. Routes that carry only a `siteId` (e.g.
 * `/settings/password`) fall back to the user's default environment for that site. Routes that
 * name no site at all (e.g. `/help`) pass an explicit {@link HostSessionScope} resolved from the
 * user's current team. Any real page mounts through `HostPageHost`, which turns this into the
 * props the `withPageProviders`-wrapped pages expect.
 */
export function useHostSession(scope?: HostSessionScope) {
  const params = useParams({ strict: false }) as { siteId?: string; siteEnv?: string };
  const namedSiteId = scope?.siteId ?? params.siteId;

  const user = useAuthStore((state) => state.data);
  // A route whose URL names no site (`/help`, mounted under `authedChromeRoute` for the sidebar)
  // runs against whichever site the session currently holds - its guard loaded that into the store.
  const storeSiteId = useEnvStore((state) => state.siteId);
  const storeSiteEnv = useEnvStore((state) => state.siteEnv);

  const siteId = namedSiteId ?? storeSiteId ?? '';

  const requestedEnv =
    scope?.siteEnv ??
    params.siteEnv ??
    (namedSiteId ? null : storeSiteEnv) ??
    (user ? (defaultSiteEnv(user, siteId) ?? 'live') : undefined);
  const siteEnv = siteId && requestedEnv ? requestedEnv : '';

  // The route guard for this page (`layouts.tsx`'s `siteRoute`, or the route's own `beforeLoad`
  // for a site-less page like `/help`) already resolved and stored this exact site/env pair before
  // this component could render, so this is a plain read - not a fetch trigger.
  const env = useEnvStore((state) =>
    state.siteId === siteId && state.siteEnv === siteEnv ? state.env : null,
  );

  return { siteId, siteEnv, env, user };
}

export function buildSiteProps(host: {
  siteId: string;
  siteEnv: string;
  env: NonNullable<HostSession['env']>;
  user: NonNullable<HostSession['user']>;
}): SiteType {
  const { env, user } = host;
  return {
    siteId: host.siteId,
    siteEnv: host.siteEnv,
    teamId: env.team.id,
    userId: user.user.id,
    userEmail: user.user.email,
    userName: user.user.name,
    isSparkStaff: !!user.user.is_spark_staff,
    plan: env.billing.plan_key,
    platform: env.platform ?? env.connected_platform,
  };
}

function buildUserProps(user: NonNullable<HostSession['user']>): UserType {
  return {
    firstName: user.user.name.split(' ')[0] ?? user.user.name,
    isSparkStaff: !!user.user.is_spark_staff,
    manageAccount: user.permissions.manageAccount,
    updateBilling: user.permissions.updateBilling,
    twoFactorEnabled: user.twoFactor.enabled,
    twoFactorRequired: user.twoFactor.required,
  };
}

/**
 * Mount a `withPageProviders`-wrapped page in the host, sourcing the `site`/`user`/`handlers` props
 * from the host session (route params + the auth/env stores). Route families each render their page
 * through this.
 */
export function HostPageHost<TPageProps extends Record<string, unknown> = Record<string, never>>({
  page: Page,
  pageProps,
  session,
}: {
  page: ComponentType<WithPageProvidersProps<TPageProps>>;
  pageProps?: TPageProps;
  /** Explicit site scope for pages whose URL names no site (e.g. `/help`). */
  session?: HostSessionScope;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { siteId, siteEnv, env, user } = useHostSession(session);

  const props = useMemo(() => {
    if (!env || !user) return null;
    const navContext: HostNavContext = { siteId, siteEnv, env };
    return {
      site: buildSiteProps({ siteId, siteEnv, env, user }),
      user: buildUserProps(user),
      handlers: {
        apiHandler,
        navigationHandler: createHostNavigationHandler(router as never, navContext),
        notificationHandler,
        authHandler,
        buildApis,
      },
    };
  }, [env, user, siteId, siteEnv, router]);

  if (!props) return null;

  return (
    <Page
      site={props.site}
      user={props.user}
      handlers={props.handlers}
      queryClient={queryClient}
      pageProps={(pageProps ?? {}) as TPageProps}
    />
  );
}
